import { type EditableCompanyLogoV2_organization$key } from '#app/__generated__/relay/EditableCompanyLogoV2_organization.graphql.js';
import { Avatar } from '#app/components/Avatars/Avatar/Avatar.js';
import { EditableAvatar } from '#app/components/EditableAvatar/EditableAvatar.js';
import { OrganizationLogoEmptyState } from '#app/components/OrganizationLogoEmptyState/OrganizationLogoEmptyState.js';
import { ProfileImageCropperContextProvider } from '#app/components/ProfileImageCropper/context/ProfileImageCropperContext.js';
import { ProfileImageCropper } from '#app/components/ProfileImageCropper/ProfileImageCropper.js';
import { useIsMediumScreen } from '#app/hooks/useIsMediumScreen.js';
import { graphql, useFragment } from '@contra/relay';
import { css, styled } from 'styled-components';

const CompanyLogoContainer = styled.div<{ $logoSize: number }>`
  ${({ $logoSize }) => css`
    display: flex;
    justify-content: center;
    align-items: center;
    height: fit-content;
    width: fit-content;
    border-radius: 50%;
    position: relative;

    &:before {
      content: '';
      background-color: rgba(255, 255, 255, 0.4);
      width: ${$logoSize + 4}px;
      height: ${$logoSize + 4}px;
      border-radius: 50%;
      position: absolute;
      top: -2px;
      left: -2px;
      clip-path: polygon(0% 118px, 100% 118px, 100% 0%, 0% 0%);
    }

    &:after {
      content: '';
      width: ${$logoSize}px;
      height: ${$logoSize}px;
      box-shadow: 0px 0px 24px 0px rgba(0, 0, 0, 0.2);
      border-radius: 50%;
      position: absolute;
      inset: 0;
    }
  `}
`;

type EditableCompanyLogoProps = {
  readonly organizationRef: EditableCompanyLogoV2_organization$key;
};

export const EditableCompanyLogoV2 = ({
  organizationRef,
}: EditableCompanyLogoProps) => {
  const data = useFragment<EditableCompanyLogoV2_organization$key>(
    graphql`
      fragment EditableCompanyLogoV2_organization on Organization {
        id
        visitorCanEdit
        logoImage {
          uid
          url
        }
      }
    `,
    organizationRef,
  );

  const isMediumScreen = useIsMediumScreen();

  const { id, visitorCanEdit } = data;

  const logoSize = isMediumScreen ? 100 : 148;

  const showEditableLogo = visitorCanEdit;

  const logoImage = data.logoImage?.uid
    ? {
        uid: data.logoImage.uid,
        url: data.logoImage.url,
      }
    : null;

  return (
    <ProfileImageCropperContextProvider>
      <CompanyLogoContainer $logoSize={logoSize}>
        {showEditableLogo ? (
          <>
            <EditableAvatar
              avatarImage={logoImage}
              avatarSize={logoSize}
              context="LOGO"
              shouldSaveProfileOnUpload
              showBorder={false}
              variant="hover"
            />
            <ProfileImageCropper organizationId={id} />
          </>
        ) : logoImage?.uid ? (
          <Avatar
            avatarImage={logoImage}
            size={logoSize}
          />
        ) : (
          <OrganizationLogoEmptyState size={logoSize} />
        )}
      </CompanyLogoContainer>
    </ProfileImageCropperContextProvider>
  );
};
